/**
 * Foto de um usuário — buscada com a sessão, não por `<img src>`.
 *
 * A foto sai por rota autenticada porque é dado pessoal, e `<img src>` não
 * manda `Authorization`. O caminho é buscar o blob e transformar em URL de
 * objeto.
 *
 * O cache em módulo não é otimização prematura: a mesma pessoa aparece em toda
 * linha de uma lista de cinquenta usuários e no cabeçalho. Sem ele, a mesma
 * foto seria baixada uma vez por componente montado, e cada `URL.createObjectURL`
 * sem `revoke` segura os bytes na memória da aba até a página recarregar.
 */

import { useEffect, useState } from 'react'

import { api } from '../api/cliente'

/** id do usuário → promessa da URL de objeto (ou null quando não tem foto). */
const cache = new Map<string, Promise<string | null>>()

function buscar(usuarioId: string): Promise<string | null> {
  const emCache = cache.get(usuarioId)
  if (emCache) return emCache
  const p = api.buscarImagem(`/api/v1/usuarios/${usuarioId}/foto`)
  cache.set(usuarioId, p)
  return p
}

/** Descarta a foto guardada — chamar depois de trocar a própria. */
export function esquecerFoto(usuarioId: string): void {
  void cache.get(usuarioId)?.then((url) => url && URL.revokeObjectURL(url))
  cache.delete(usuarioId)
}

export function iniciaisDe(nome: string): string {
  const partes = nome.trim().split(/\s+/).filter(Boolean)
  if (partes.length === 0) return '?'
  if (partes.length === 1) return partes[0]!.slice(0, 2).toUpperCase()
  return `${partes[0]![0]}${partes[partes.length - 1]![0]}`.toUpperCase()
}

export interface AvatarProps {
  usuarioId: string
  nome: string
  /** Falso pula a busca: a listagem já sabe quem não tem foto. */
  temFoto?: boolean
  tamanho?: number
  className?: string
}

export function Avatar({ usuarioId, nome, temFoto = true, tamanho = 36, className = '' }: AvatarProps) {
  const [url, setUrl] = useState<string | null>(null)

  useEffect(() => {
    if (!temFoto) {
      setUrl(null)
      return
    }
    let vivo = true
    void buscar(usuarioId).then((u) => {
      if (vivo) setUrl(u)
    })
    return () => {
      vivo = false
    }
  }, [usuarioId, temFoto])

  const estilo = { width: tamanho, height: tamanho, fontSize: Math.round(tamanho / 2.6) }

  if (!url) {
    return (
      <span
        aria-hidden="true"
        style={estilo}
        className={`inline-flex shrink-0 items-center justify-center rounded-full bg-marca-100 font-semibold text-marca-700 ${className}`}
      >
        {iniciaisDe(nome)}
      </span>
    )
  }

  return (
    <img
      src={url}
      alt=""
      style={estilo}
      className={`inline-block shrink-0 rounded-full object-cover ${className}`}
    />
  )
}
